import { useNavigate } from '@tanstack/react-router';
import { getAttemptedTransitionUrl, setAttemptedTransitionUrl } from '@customerio/hydra-nav-state';
import { useEffect, useRef } from 'react';

/** Thin wrapper around `window.location.assign` — use this so tests can mock the module instead of fighting the non-configurable browser API. */
export function navigateExternal(url: string): void {
  window.location.assign(url);
}

type NavigateWithTransitionUrlProps = {
  to: string;
  replace?: boolean;
  /** Query params to carry over to the destination (e.g. preserving `?origin`). */
  search?: Record<string, string>;
  /**
   * Whether to save the current URL before navigating.
   * When true, the current URL will be saved and can be used
   * to redirect back after authentication or other flows.
   */
  saveCurrentUrl?: boolean;
};

/**
 * A generic Navigate component that can optionally save the current URL
 * before redirecting. This is useful for auth flows where you want to
 * redirect back to the original page after login.
 */
export function Navigate({ saveCurrentUrl = false, ...props }: NavigateWithTransitionUrlProps) {
  const navigate = useNavigate();
  const hasNavigated = useRef(false);

  useEffect(() => {
    if (hasNavigated.current) {
      return;
    }

    hasNavigated.current = true;

    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    if (saveCurrentUrl && getAttemptedTransitionUrl() !== window.location.href) {
      setAttemptedTransitionUrl(window.location.href);
    }

    void navigate({
      to: props.to,
      replace: props.replace,
      ...(props.search != null ? { search: props.search } : {}),
    });
  }, [navigate, props.replace, props.to, props.search, saveCurrentUrl]);

  return null;
}
